import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FormatIndianNumber } from "../utiles/FormatIndianNumber";
import TableLayout from "./TableLayout";
import useLocale from "../locales";

const EarlyLoanTable = ({
  showTable,
  historyData = null,
  shouldGenerateReport,
  setShouldGenerateReport,calcDone
}) => {
  const { textData } = useLocale();
  const { loanAmount, timePeriod, intrestRate, prePayment, timeUnit } =
    useSelector((state) => state.earlyLoanCalculator);
  const [reportData, setReportData] = useState([]);

  useEffect(() => {
    if (historyData) {
      generateHistoryreport();
      // } else if (showTable && loanAmount && timePeriod && intrestRate && prePayment !== null && prePayment !== undefined) {
      //   genrateReportData();
    }
  }, [
    showTable,
    loanAmount,
    timePeriod,
    intrestRate,
    prePayment,
    timeUnit,
    historyData,
    shouldGenerateReport,
  ]);

  useEffect(() => {
    if (shouldGenerateReport) {
      genrateReportData();
      setShouldGenerateReport(false);
    }else if (!calcDone) {
    setReportData([]); 
    generateHistoryreport();
  }
}, [shouldGenerateReport, calcDone, historyData]);


  const generateHistoryreport = () => {
    if (!Array.isArray(historyData) || historyData.length === 0) {
      return;
    }
    const earlyData = historyData[0];

    const monthlyRate = earlyData.interestRate / 12 / 100;

    // Fix: Use the saved months data directly from history instead of recalculating
    const totalMonths = earlyData.months || earlyData.tenure;

    // Determine the display unit from the saved data
    // If months equals tenure, it was saved as months; if months equals tenure*12, it was saved as years
    const displayUnit =
      earlyData.months === earlyData.tenure ? "Month" : "Year";

    const loanAmountNum = parseFloat(earlyData.totalInvestment);
    const prePaymentNum = parseFloat(earlyData.prePaymentAmount || 0);

    // Calculate original EMI
    const originalEMI = calculateEMI(
      loanAmountNum,
      earlyData.interestRate,
      totalMonths
    );

    const newPrincipal = loanAmountNum - prePaymentNum;
    const newEMI = calculateEMI(
      newPrincipal,
      earlyData.interestRate,
      totalMonths
    );

    let balance = newPrincipal;
    const data = [];
    for (let i = 1; i <= totalMonths; i++) {
      const interest = balance * monthlyRate;
      const principalPayment = newEMI - interest;
      balance -= principalPayment;

      if (balance < 0) {
        balance = 0;
      }

      data.push({
        month: i,
        monthlyEmi: newEMI,
        principalPaid: principalPayment,
        interestPaid: interest,
        remainingBalance: balance,
      });

      // Break if loan is paid off
      if (balance <= 0) break;
    }

    // Group by years only if the original calculation was in years
    if (displayUnit === "Year") {
      const yearlyData = [];
      const years = Math.ceil(totalMonths / 12);

      for (let year = 1; year <= years; year++) {
        const startMonth = (year - 1) * 12;
        const endMonth = Math.min(year * 12, data.length);
        const yearData = data.slice(startMonth, endMonth);

        if (yearData.length === 0) break;

        yearlyData.push({
          month: year,
          monthlyEmi: newEMI,
          principalPaid: yearData.reduce((sum, m) => sum + m.principalPaid, 0),
          interestPaid: yearData.reduce((sum, m) => sum + m.interestPaid, 0),
          remainingBalance: yearData[yearData.length - 1].remainingBalance,
        });
      }
      setReportData(yearlyData);
    } else {
      // For monthly view, show the data as is
      setReportData(data);
    }
  };

  // Helper function to calculate EMI
  const calculateEMI = (principal, rate, months) => {
    const monthlyRate = rate / 12 / 100;
    return (
      (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) /
      (Math.pow(1 + monthlyRate, months) - 1)
    );
  };

  // Add the missing genrateReportData function for current calculations
  const genrateReportData = () => {
    const monthlyRate = intrestRate / 12 / 100;
    const totalMonths = timeUnit === "Year" ? timePeriod * 12 : timePeriod;
    const loanAmountNum = parseFloat(loanAmount);
    const prePaymentNum = parseFloat(prePayment || 0);

    const newPrincipal = loanAmountNum - prePaymentNum;
    const newEMI = calculateEMI(newPrincipal, intrestRate, totalMonths);

    let balance = newPrincipal;
    const data = [];
    for (let i = 1; i <= totalMonths; i++) {
      const interest = balance * monthlyRate;
      const principalPayment = newEMI - interest;
      balance -= principalPayment;

      if (balance < 0) {
        balance = 0;
      }

      data.push({
        month: i,
        monthlyEmi: newEMI,
        principalPaid: principalPayment,
        interestPaid: interest,
        remainingBalance: balance,
      });

      if (balance <= 0) break;
    }

    if (timeUnit === "Year") {
      const yearlyData = [];
      const years = Math.ceil(totalMonths / 12);

      for (let year = 1; year <= years; year++) {
        const startMonth = (year - 1) * 12;
        const endMonth = Math.min(year * 12, data.length);
        const yearData = data.slice(startMonth, endMonth);

        if (yearData.length === 0) break;

        yearlyData.push({
          month: year,
          monthlyEmi: newEMI,
          principalPaid: yearData.reduce((sum, m) => sum + m.principalPaid, 0),
          interestPaid: yearData.reduce((sum, m) => sum + m.interestPaid, 0),
          remainingBalance: yearData[yearData.length - 1].remainingBalance,
        });
      }
      setReportData(yearlyData);
    } else {
      setReportData(data);
    }
  };

  // Determine the correct time unit for column headers
  const getDisplayTimeUnit = () => {
    if (historyData && historyData.length > 0) {
      const earlyData = historyData[0];
      return earlyData.months === earlyData.tenure ? "Month" : "Year";
    }
    return timeUnit;
  };

  const displayTimeUnit = getDisplayTimeUnit();

  const columns = [
    {
      key: "month",
      label:
        displayTimeUnit === "Year"
          ? textData.tableLabels.year
          : textData.tableLabels.month,
      format: (value) => value,
    },
    {
      key: "monthlyEmi",
      label: `${textData.tableLabels.monthlyEmi} (${textData.currency})`,
      format: (value) => `${FormatIndianNumber(Math.round(value))}`,
    },
    {
      key: "principalPaid",
      label: `${textData.tableLabels.principlePaid} (${textData.currency})`,
      format: (value) => `${FormatIndianNumber(Math.round(value))}`,
    },
    {
      key: "interestPaid",
      label: `${textData.tableLabels.intrestPaid} (${textData.currency})`,
      format: (value) => `${FormatIndianNumber(Math.round(value))}`,
    },
    {
      key: "remainingBalance",
      label: `${textData.tableLabels.remainingBalance} (${textData.currency})`,
      format: (value) => `${FormatIndianNumber(Math.round(value))}`,
    },
  ];

  return (
    <TableLayout
      showTable={showTable}
      title={`${textData.filterDropDown.earlyLoanCalc} ${textData.report}`}
            data={reportData}

      columns={columns}
      analyticsEventName="download_pl_report"
      reportTitle={textData.reportTitle.earlyLoan}
      fileName="early"
    />
  );
};

export default EarlyLoanTable;
